// Translates call-state transitions into AudioBackend calls.
//
// Pure logic over an injected AudioBackend — no React, no Web Audio direct.
// Owns one piece of state: the active gated-tone Cancel (ringback or
// incoming ring; mutually exclusive). One-shot tones (chirp/beep) finish
// on their own; we don't track them.

import type { AudioBackend, Cancel, RingbackOpts } from './audioBackend'
import { getDefaultBackend } from './audioBackend'

export type CallState = 'dialing' | 'ringing' | 'active'
export type CallDirection = 'outgoing' | 'incoming'

export interface TrackState {
  callId: string
  state: CallState
  direction: CallDirection
}

export interface Transition {
  prev: TrackState | null
  next: TrackState | null
}

export const RINGBACK = { freq: 425, volume: 0.4, onMs: 1000, offMs: 4000 } as const
export const INCOMING_RING = { freq: 520, volume: 0.4, onMs: 1200, offMs: 2400 } as const
export const CHIRP = { freq: 880, durationMs: 150, volume: 0.3, attackMs: 10, releaseMs: 80 } as const
export const BEEP = { freq: 480, durationMs: 400, volume: 0.5, attackMs: 20, releaseMs: 20 } as const

export class CallToneController {
  // Holds cancel for whichever gated tone is currently scheduled (RINGBACK
  // for outgoing dial, INCOMING_RING for incoming ringing). Mutually
  // exclusive: relay never produces RINGING for outgoing direction.
  private activeGatedTone: Cancel | null = null
  private readonly backend: AudioBackend

  constructor(backend: AudioBackend) {
    this.backend = backend
  }

  onTransition(t: Transition): void {
    const { prev, next } = t

    // Same call_id → state changed within the same call.
    if (prev && next && prev.callId === next.callId) {
      this.handleSameCall(prev, next)
      return
    }

    // call_id changed (or one side is null) → end old, start new.
    if (prev) this.handleEnd(prev)
    if (next) this.handleStart(next)
  }

  private handleSameCall(prev: TrackState, next: TrackState): void {
    if (prev.state === next.state) return

    if (prev.state === 'dialing' && next.state === 'active') {
      this.cancelGatedTone()
      if (prev.direction === 'outgoing') this.backend.playTone(CHIRP)
      return
    }

    if (prev.state === 'ringing' && next.state === 'active') {
      // Incoming pickup — stop the ring, no chirp (minimal scope).
      this.cancelGatedTone()
    }
  }

  private handleStart(next: TrackState): void {
    if (next.state === 'dialing' && next.direction === 'outgoing') {
      this.startGatedTone(RINGBACK)
    } else if (next.state === 'ringing' && next.direction === 'incoming') {
      this.startGatedTone(INCOMING_RING)
    }
    // active (reload mid-call) → silent.
  }

  private handleEnd(prev: TrackState): void {
    this.cancelGatedTone()
    // Outgoing rejected/hung-up gets a beep. Incoming missed (ringing→idle) stays silent.
    if (prev.state !== 'ringing') {
      this.backend.playTone(BEEP)
    }
  }

  private startGatedTone(opts: RingbackOpts): void {
    this.cancelGatedTone()
    this.activeGatedTone = this.backend.playRingback(opts)
  }

  private cancelGatedTone(): void {
    if (this.activeGatedTone) {
      this.activeGatedTone()
      this.activeGatedTone = null
    }
  }

  cancelAll(): void {
    this.cancelGatedTone()
  }
}

let defaultController: CallToneController | null = null

export function getDefaultController(): CallToneController {
  if (!defaultController) {
    defaultController = new CallToneController(getDefaultBackend())
  }
  return defaultController
}

/** Test helper. Don't use in production code. */
export function __resetDefaultControllerForTests(): void {
  defaultController?.cancelAll()
  defaultController = null
}
